import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';

const CircularProgress = ({
  percentage,
  rangeEnd,
  displayText,
  size = 160,
  strokeWidth = 12,
  color = '#6366f1',
  rangeColor,
  bgColor = '#e5e7eb',
  label,
  animated = true,
}) => {
  const [animatedStart, setAnimatedStart] = useState(0);
  const [animatedEnd, setAnimatedEnd] = useState(0);

  const clampedStart = Math.min(100, Math.max(0, percentage));
  const clampedEnd = rangeEnd !== undefined
    ? Math.min(100, Math.max(0, rangeEnd))
    : clampedStart;

  const isRange = rangeEnd !== undefined;

  useEffect(() => {
    if (!animated) {
      setAnimatedStart(clampedStart);
      setAnimatedEnd(clampedEnd);

      return;
    }

    if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
      let start = null;
      const duration = 1000;
      const prevStart = animatedStart;
      const prevEnd = animatedEnd;

      const animate = (timestamp) => {
        if (!start) start = timestamp;

        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - (1 - progress) ** 3;

        setAnimatedStart(prevStart + (clampedStart - prevStart) * eased);
        setAnimatedEnd(prevEnd + (clampedEnd - prevEnd) * eased);

        if (progress < 1) {
          window.requestAnimationFrame(animate);
        }
      };

      window.requestAnimationFrame(animate);
    }
  }, [clampedStart, clampedEnd, animated, animatedStart, animatedEnd]);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const singleOffset = circumference - (animatedStart / 100) * circumference;

  const rangeStartOffset = circumference - (animatedStart / 100) * circumference;
  const rangeEndOffset = circumference - (animatedEnd / 100) * circumference;

  const getDisplayText = () => {
    if (displayText) return displayText;

    if (isRange) {
      return `${Math.round(animatedStart)}-${Math.round(animatedEnd)}%`;
    }

    return `${Math.round(animatedStart)}%`;
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.progressWrapper}
        style={{ width: size, height: size }}
      >
        <svg
          width={size}
          height={size}
          className={styles.svg}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={bgColor}
            strokeWidth={strokeWidth}
          />

          {isRange ? (
            <>
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={rangeColor || color}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={rangeEndOffset}
                strokeLinecap="round"
                opacity={0.3}
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={rangeStartOffset}
                strokeLinecap="round"
              />
            </>
          ) : (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={singleOffset}
              strokeLinecap="round"
            />
          )}
        </svg>

        <div className={styles.content}>
          <span
            className={styles.title}
            style={{
              fontSize: size * 0.15,
              lineHeight: 1.2,
            }}
          >
            {getDisplayText()}
          </span>
          {label && (
            <span
              className={styles.subtitle}
              style={{ fontSize: size * 0.09, marginTop: 2 }}
            >
              {label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CircularProgress;
